import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AncillaryOptionsFileds, AncillaryType } from 'interfaces/ancillary';
import {
  Passenger,
  PassengerError,
  PassengerErrors,
  Passengers,
  PassengerTypeCode,
  Payment,
} from 'interfaces/booking';
import { CurrencyType } from 'interfaces/payment';
import { RootState } from 'store';
import {
  getAncillaryFareInSelectedCurrency,
  getTotalFareInSelectedCurrency,
} from 'utils/utils';
import { validateInvoiceInput, validatePassengerInput } from 'validations';

export const initialPassenger: Passenger = {
  email: '',
  namePrefix: 'Mr',
  firstName: '',
  middleName: '',
  lastName: '',
  phoneNumber: {
    value: '',
    formattedValue: '',
  },
  birthYear: null,
  birthMonth: null,
  birthDay: null,
  address1: '',
  address2: '',
  postalCode: '',
  countryCode: {
    name: '',
    alpha2Code: '',
  },
  city: '',
  typeCode: 'ADT',
  documentCountry: {
    alpha2Code: '',
    name: '',
  },
  documentType: 'Passport',
  documentId: '',
  documentExpirationDay: null,
  documentExpirationMonth: null,
  documentExpirationYear: null,
  ancillary: {
    inbound: {},
    outbound: {},
    full: {},
  },
};

export const initialPaymentData = {
  cardName: '',
  cardNumber: '',
  ccv: '',
  expirationMonth: '',
  expirationYear: '',
};

export interface IBookingState {
  passengers: Passengers;
  passengerErrors: PassengerErrors;
  invoiceData: Passenger;
  invoiceDataError: PassengerError | null;
  isSavePassengersToProfile: boolean;
  isInvoiceDataSame: boolean;
  paymentData: Payment;
  isLoading: boolean;
}

const initialState: IBookingState = {
  passengers: {
    adults: [initialPassenger],
    children: [],
    infants: [],
  },
  passengerErrors: {},
  invoiceData: initialPassenger,
  invoiceDataError: null,
  isSavePassengersToProfile: false,
  isInvoiceDataSame: true,
  paymentData: initialPaymentData,
  isLoading: false,
};

const booking = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    setPassengerNums: (
      state,
      action: PayloadAction<{
        num: 1 | -1;
        passenger: keyof typeof state.passengers;
      }>,
    ) => {
      const { num, passenger } = action.payload;
      if (num === 1)
        if (
          (passenger === 'infants' &&
            state.passengers.adults.length <=
              state.passengers.infants.length) ||
          (passenger !== 'infants' &&
            state.passengers.adults.length + state.passengers.children.length >=
              9)
        ) {
          return;
        } else {
          let passengerTypeCode: PassengerTypeCode = 'ADT';
          if (passenger === 'children') passengerTypeCode = 'CHD';
          if (passenger === 'infants') passengerTypeCode = 'INF';
          state.passengers[passenger].push({
            ...initialPassenger,
            typeCode: passengerTypeCode,
          });
        }
      if (num === -1)
        if (
          (passenger === 'adults' && state.passengers.adults.length <= 1) ||
          (passenger !== 'adults' && state.passengers[passenger].length <= 0)
        )
          return;
        else state.passengers[passenger].pop();
    },
    setInitialPassenger: state => {
      state.passengers = {
        adults: [initialPassenger],
        children: [],
        infants: [],
      };
      state.invoiceData = initialPassenger;
      state.isInvoiceDataSame = true;
      state.invoiceDataError = null;
      state.passengerErrors = {};
    },
    setPassenger: (
      state,
      action: PayloadAction<{
        type: keyof Passengers;
        idx: number;
        key: keyof Passenger;
        value: any;
      }>,
    ) => {
      const { type, idx, key, value } = action.payload;
      if (idx >= state.passengers[type].length) return;
      const newPassengers = [...state.passengers[type]];
      const updatedPassenger = {
        ...newPassengers[idx],
        [key]: value,
      };
      newPassengers[idx] = updatedPassenger;
      state.passengers = {
        ...state.passengers,
        [type]: newPassengers,
      };
    },
    setBookingPassenger: (
      state,
      action: PayloadAction<{ key: keyof Passenger; value: any }>,
    ) => {
      const { key, value } = action.payload;
      state.invoiceData = {
        ...state.invoiceData,
        [key]: value as never,
      };
    },

    setIsSaveDataToProfile: (state, action: PayloadAction<boolean>) => {
      state.isSavePassengersToProfile = action.payload;
    },
    setIsInvoiceDataSame: (state, action: PayloadAction<boolean>) => {
      state.isInvoiceDataSame = action.payload;
      if (action.payload) {
        state.invoiceDataError = null;
      }
    },
    setPassengerErrors: (state, action: PayloadAction<PassengerErrors>) => {
      state.passengerErrors = action.payload;
    },
    setInvoiceErrors: (state, action: PayloadAction<PassengerError | null>) => {
      state.invoiceDataError = action.payload;
    },
    setPaymentData: (
      state,
      action: PayloadAction<{ key: keyof Payment; value: any }>,
    ) => {
      const { key, value } = action.payload;
      state.paymentData = {
        ...state.paymentData,
        [key]: value,
      };
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    validatePassengerInput: (
      state,
      action: PayloadAction<{
        key: keyof Passenger;
        p: Passenger;
        id: string;
        value?: any;
      }>,
    ) => {
      const validaitonResult = validatePassengerInput(
        action.payload,
        state.passengerErrors,
      );
      if (validaitonResult) {
        state.passengerErrors = validaitonResult;
      }
    },
    validateInvoiceInput: (
      state,
      action: PayloadAction<{ key: keyof Passenger; value?: any }>,
    ) => {
      state.invoiceDataError = validateInvoiceInput(
        { ...action.payload, p: state.invoiceData },
        state.invoiceDataError,
      );
    },
  },
});

export const selectNumOfPassengers = ({
  booking: { passengers },
}: RootState): number =>
  passengers.adults.length +
  passengers.children.length +
  passengers.infants.length;

export const getPassengersTotalAncillaryPrice = (
  currency: CurrencyType,
  passengers: Passengers,
) => {
  // Object which stores the sum of each ancillary type.
  // The key is the ancillary type, the value is the sum and peice contains the
  // total number of ancillaries for the given type
  const ancillaries: {
    [key in AncillaryType]?: {
      value: number;
      piece: number;
      title: string;
    };
  } = {};

  // Calculates the sum of the ancillaries by the ancillary type for all the passengers
  [...passengers.adults, ...passengers.infants, ...passengers.children].forEach(
    el => {
      // Loop the ancillary keys (outbound, inbound, return)
      if (el.ancillary) {
        Object.entries(el.ancillary).forEach(([routeKey, routeEntry]) => {
          // For each ancillary route (inbound, outboundm full) loop their ancillary types (baggage, priority_boarding, etc..)
          if (routeEntry) {
            Object.entries(routeEntry).forEach(([typeKey, typeEntry]) => {
              if (routeKey !== 'full') {
                const curr = el.ancillary.full[typeKey as AncillaryType];
                if (!curr || Object.keys(curr).length === 0) {
                  const currSum = sumAncillaryTypes(
                    ancillaries[typeKey as AncillaryType]?.value || 0,
                    ancillaries[typeKey as AncillaryType]?.piece || 0,
                    currency,
                    typeEntry,
                  );
                  ancillaries[typeKey as AncillaryType] = {
                    value: currSum.value,
                    piece: currSum.piece,
                    title: typeEntry?.category || '',
                  };
                }
              } else {
                const currSum = sumAncillaryTypes(
                  ancillaries[typeKey as AncillaryType]?.value || 0,
                  ancillaries[typeKey as AncillaryType]?.piece || 0,
                  currency,
                  typeEntry,
                );
                ancillaries[typeKey as AncillaryType] = {
                  value: currSum.value,
                  piece: currSum.piece,
                  title: typeEntry?.category || '',
                };
              }
            });
          }
        });
      }
    },
  );

  return ancillaries;
};

const sumAncillaryTypes = (
  value: number,
  piece: number,
  currency: CurrencyType,
  typeEntry: AncillaryOptionsFileds,
) => {
  // Retrieving the ancillary fare in the selected currency
  const fareInCurrency = Math.ceil(
    getAncillaryFareInSelectedCurrency(
      currency,
      typeEntry?.price_in_preferred_currencies,
    ),
  );

  return {
    value: value + fareInCurrency,
    piece:
      piece +
      (!typeEntry
        ? 0
        : !typeEntry.total?.number_of_bags
        ? 1
        : parseFloat(typeEntry?.total?.number_of_bags || '0')),
  };
};

export const selectIsValidateError = ({ booking: b }: RootState): boolean => {
  return (
    (b.passengerErrors && Object.keys(b.passengerErrors).length > 0) ||
    (!b.isInvoiceDataSame &&
      b.invoiceDataError !== null &&
      Object.keys(b.invoiceDataError).length > 0)
  );
};

// need to wrap some of the default functions to a generic function
// because we need type safety
const { actions: bookingActions } = booking;
export const actions = {
  ...bookingActions,
  setPassenger: <K extends keyof Passenger>(payload: {
    type: keyof Passengers;
    idx: number;
    key: K;
    value: Passenger[K];
  }) => bookingActions.setPassenger(payload),
  setBookingPassenger: <K extends keyof Passenger>(payload: {
    key: K;
    value: Passenger[K];
  }) => bookingActions.setBookingPassenger(payload),
};

export default booking.reducer;
